import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { openDialogAction } from "@icarius-common/dialog/actions";
import {
    downloadImportStructureAction,
    getInitialEntryPerConcept,
    getEntryPerConcept,
    createEntryPerConcept,
    modifyEntryPerConcept,
    deleteEntryPerConcept,
    getEntryPerConceptMyPeopleGrid,
    duplicateEntryPerConcept,
} from "../actions";
import {
    getEntryStructures,
    getIsLoading,
    getColumnTypes,
    getRows,
    getConceptsTypes,
    getConcepts,
    getSelects,
    getSocietyPeople,
    getColaborators,
    getCurrencyLocalization,
    getDateFormat,
    getQuantityPerUsedEntryStructure,
    getDecimals,
    getStructureConcepts,
} from "../selectors";
import { useHistory } from "react-router-dom";
import paths from "@icarius-router/paths";
import { getLocalizedString } from "@icarius-localization/strings";
import { getColumnDefs, getFields } from "./columns";
import { RESET_STATE } from "../actionTypes";

const useEntryPerConcept = () => {
    const mesesKeyValue = [
        { key: "1", value: getLocalizedString("january") },
        { key: "2", value: getLocalizedString("february") },
        { key: "3", value: getLocalizedString("march") },
        { key: "4", value: getLocalizedString("april") },
        { key: "5", value: getLocalizedString("may") },
        { key: "6", value: getLocalizedString("june") },
        { key: "7", value: getLocalizedString("july") },
        { key: "8", value: getLocalizedString("august") },
        { key: "9", value: getLocalizedString("september") },
        { key: "10", value: getLocalizedString("october") },
        { key: "11", value: getLocalizedString("november") },
        { key: "12", value: getLocalizedString("december") },
    ];

    const months = mesesKeyValue.map((mes) => mes.value);

    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();

    const [createEditDialogIsOpen, setCreateEditDialogIsOpen] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [selectedEntryStructureCode, setSelectedEntryStructureCode] = useState(location?.state?.structureKey || "");
    const [myPeopleGridDialogIsOpen, setMyPeopleGridDialogIsOpen] = useState(false);
    const [rowDataToReplicate, setRowDataToReplicate] = useState(null);
    const [addConceptStructureIsOpen, setAddConceptStructureIsOpen] = useState(false);
    const [duplicateConceptIsOpen, setDuplicateConceptIsOpen] = useState(false);

    const columnTypes = useSelector(getColumnTypes);
    const gridData = useSelector(getRows);
    const entryStructures = useSelector(getEntryStructures);
    const conceptsTypes = useSelector(getConceptsTypes);
    const concepts = useSelector(getConcepts);
    const selects = useSelector(getSelects);
    const structureConcepts = useSelector(getStructureConcepts);
    const isLoading = useSelector(getIsLoading);
    const societyPeople = useSelector(getSocietyPeople);
    const colaborators = useSelector(getColaborators);
    const locale = useSelector(getCurrencyLocalization);
    const dateFormat = useSelector(getDateFormat);
    const quantityPerUsedEntryStructure = useSelector(getQuantityPerUsedEntryStructure);
    const decimals = useSelector(getDecimals);

    const getEntryStructure = (selectedEntryStructureCode) => {
        const entryStructure = entryStructures.find((entry) => entry.code === selectedEntryStructureCode);
        return entryStructure || { periodicity: "" };
    };

    const getPeriodicity = (selectedEntryStructureCode) => {
        if (selectedEntryStructureCode && columnTypes.length) {
            const element = columnTypes.find(
                (el) => el.key === getEntryStructure(selectedEntryStructureCode).periodicity
            );
            if (element) {
                return element.value;
            }
        }
        return "";
    };

    const [selectedEntryStructurePeriodicity, setSelectedEntryStructurePeriodicity] = useState(
        getPeriodicity(location?.state?.structureKey)
    );

    const columnDefs = getColumnDefs(getEntryStructure(selectedEntryStructureCode), decimals);
    const fields = getFields(getEntryStructure(selectedEntryStructureCode), decimals);

    useEffect(() => {
        dispatch(getInitialEntryPerConcept());
    }, [dispatch]);

    useEffect(() => {
        if (selectedEntryStructureCode !== "") {
            dispatch(getEntryPerConcept(selectedEntryStructureCode));
        }
    }, [dispatch, selectedEntryStructureCode]);

    const handleGoBack = () => {
        if (location?.state?.from) {
            history.push(paths.assetsDiscountsSummary);
        } else {
            dispatch({ type: RESET_STATE });
            history.push(paths.assetsDiscounts);
        }
    };

    const handleChangeEntryStructure = (entryStructure) => {
        setSelectedEntryStructureCode(entryStructure);
        setSelectedEntryStructurePeriodicity(getPeriodicity(entryStructure));
        setRowDataToReplicate(null);
    };

    const handleSetUsers = (employeeCodes) => {
        dispatch(
            duplicateEntryPerConcept({
                entryStructure: selectedEntryStructureCode,
                employeeCodes,
                internalCode: rowDataToReplicate["Código interno del registro"],
            })
        );
    };

    const formatData = (data) => {
        let modifiedRow = { ...data };

        // Aca arranco a poner lo que hace falta
        const entryStructure = getEntryStructure(selectedEntryStructureCode);

        modifiedRow["Periodicidad"] = entryStructure.periodicity;

        for (let [key, value] of Object.entries(modifiedRow)) {
            if (key.includes("(hhmm)")) {
                delete modifiedRow[key];
                let newKey = key.replace("(hhmm)", "(hh:mm)");
                modifiedRow[newKey] = value;
            }

            if (months.includes(key)) {
                modifiedRow[key] = value === "Y";
            }
        }

        return modifiedRow;
    };

    const handleCreateEntryPerConcept = (data) => {
        let formattedData = formatData(data);
        dispatch(
            createEntryPerConcept({
                entryStructure: selectedEntryStructureCode,
                item: formattedData,
            })
        ).then((resp) => resp.status === 200 && handleCloseCreateEditDialog());
    };

    const handleCloseCreateEditDialog = () => {
        setCreateEditDialogIsOpen(false);
        setSelectedDocument(null);
    };

    const handleModifyEntryPerConcept = (data) => {
        let formattedData = formatData(data);

        dispatch(
            modifyEntryPerConcept({
                entryStructure: selectedEntryStructureCode,
                item: formattedData,
            })
        ).then((resp) => resp.status === 200 && handleCloseCreateEditDialog());
    };

    const handleOpenDeleteDialog = (gridRef) => {
        let rows = gridRef.api.getSelectedRows();
        if (rows.length < 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectAtLeastOneRow"), severity: "warning" }));
            return;
        }

        const codes = rows.map((el) => el["Código interno del registro"]);

        dispatch(
            openDialogAction({
                title: getLocalizedString("atention"),
                msg: getLocalizedString("doYouWantToDeleteSelectedRows"),
                onConfirm: () =>
                    dispatch(
                        deleteEntryPerConcept({ entryStructure: selectedEntryStructureCode, internalCodes: codes })
                    ),
            })
        );
    };

    const handleExportStructure = () => {
        if (selectedEntryStructureCode === "") {
            dispatch(openSnackbarAction({ msg: getLocalizedString("youMustSelectAStructure"), severity: "warning" }));
            return;
        }

        dispatch(downloadImportStructureAction({ structure: selectedEntryStructureCode, type: "C", empty: false }));
    };

    const handleOpenMyPeopleGridDialog = (gridRef) => {
        let documents = gridRef.api.getSelectedRows();
        if (documents.length !== 1) {
            dispatch(
                openSnackbarAction({ msg: getLocalizedString("mustSelectARecordToReplicate"), severity: "warning" })
            );
            return;
        }
        setRowDataToReplicate(documents[0]);

        if (colaborators.length === 0) {
            dispatch(getEntryPerConceptMyPeopleGrid()).then(
                (resp) => resp.status === 200 && setMyPeopleGridDialogIsOpen(true)
            );
        } else {
            setMyPeopleGridDialogIsOpen(true);
        }
    };

    const handleCloseMyPeopleGridDialog = () => {
        setMyPeopleGridDialogIsOpen(false);
    };

    const handleOpenCreateDialog = () => {
        setSelectedDocument(null);

        if (societyPeople.length === 0) {
            dispatch(getEntryPerConceptMyPeopleGrid()).then(
                (resp) => resp.status === 200 && setCreateEditDialogIsOpen(true)
            );
        } else {
            setCreateEditDialogIsOpen(true);
        }
    };

    const handleOpenEditDialog = (gridRef) => {
        let documents = gridRef.api.getSelectedRows();
        if (documents.length !== 1) {
            dispatch(openSnackbarAction({ msg: getLocalizedString("mustSelectOneRow"), severity: "warning" }));
            return;
        }

        setSelectedDocument(documents[0]);

        if (societyPeople.length === 0) {
            dispatch(getEntryPerConceptMyPeopleGrid()).then(
                (resp) => resp.status === 200 && setCreateEditDialogIsOpen(true)
            );
        } else {
            setCreateEditDialogIsOpen(true);
        }
    };

    const handleRowClick = (row) => {
        setSelectedDocument(row.data);

        if (societyPeople.length === 0) {
            dispatch(getEntryPerConceptMyPeopleGrid()).then(
                (resp) => resp.status === 200 && setCreateEditDialogIsOpen(true)
            );
        } else {
            setCreateEditDialogIsOpen(true);
        }
    };

    const handleOpenAddConceptStructure = () => {
        setAddConceptStructureIsOpen(true);
    };

    const handleCloseAddConceptStructure = () => {
        setAddConceptStructureIsOpen(false);
    };

    const handleOpenDuplicatConcept = () => {
        setDuplicateConceptIsOpen(true);
    };

    const handleCloseDuplicatConcept = () => {
        setDuplicateConceptIsOpen(false);
    };

    const state = {
        createEditDialogIsOpen,
        selectedDocument,
        selectedEntryStructureCode,
        myPeopleGridDialogIsOpen,
        selectedEntryStructurePeriodicity,
        addConceptStructureIsOpen,
        duplicateConceptIsOpen,
    };

    const handlers = {
        handleSetUsers,
        handleOpenDeleteDialog,
        handleChangeEntryStructure,
        handleGoBack,
        handleExportStructure,
        handleOpenMyPeopleGridDialog,
        handleCloseMyPeopleGridDialog,
        handleOpenCreateDialog,
        handleOpenEditDialog,
        handleCloseCreateEditDialog,
        handleCreateEntryPerConcept,
        handleModifyEntryPerConcept,
        handleRowClick,
        handleOpenAddConceptStructure,
        handleCloseAddConceptStructure,
        handleOpenDuplicatConcept,
        handleCloseDuplicatConcept,
    };

    return {
        months,
        societyPeople,
        quantityPerUsedEntryStructure,
        entryStructures,
        dateFormat,
        gridData,
        columnDefs,
        fields,
        isLoading,
        state,
        handlers,
        colaborators,
        locale,
        selects,
        structureConcepts,
        concepts,
        conceptsTypes,
        decimals,
    };
};

export default useEntryPerConcept;
